import { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const PAGE_SIZE = 20;

export default function BankOffersPage() {
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);

  const [selectedBank, setSelectedBank] = useState(null);
  const [offersCache, setOffersCache] = useState({});
  const [bankLink, setBankLink] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const formatAmount = (val) => {
    if (!val) return null;
    const str = val.toString().trim();
    return str.startsWith("$") ? str : `$${str}`;
  };

  useEffect(() => {
    const loadBanks = async () => {
      setBanksLoading(true);
      try {
        const snapshot = await getDocs(collection(db, "banks"));
        const list = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        setBanks(list);
      } catch (err) {
        console.error("Failed to load banks:", err);
      } finally {
        setBanksLoading(false);
      }
    };
    loadBanks();
  }, []);

  useEffect(() => {
    if (!selectedBank && banks.length > 0) {
      setSelectedBank(banks[0].id);
    }
  }, [selectedBank, banks]);

  useEffect(() => {
    if (!selectedBank) return;

    const fetchOffersAndBankLink = async () => {
      try {
        if (!offersCache[selectedBank]) {
          // Fetch the single bank document with offers array
          const bankDocRef = doc(db, "bank_offers", selectedBank);
          const bankDocSnap = await getDoc(bankDocRef);
          if (!bankDocSnap.exists()) {
            setOffersCache((prev) => ({ ...prev, [selectedBank]: [] }));
            setBankLink("");
            return;
          }

          const data = bankDocSnap.data();
          let offersList = Array.isArray(data.offers) ? data.offers : [];

          // Filter out expired offers based on expiryDttm
          const now = new Date();
          offersList = offersList.filter((offer) => {
            let expiry = null;
            if (offer.expiryDttm?.toDate) {
              expiry = offer.expiryDttm.toDate();
            } else if (offer.expiryDttm) {
              expiry = new Date(offer.expiryDttm);
            }
            return !expiry || expiry > now;
          });

          setOffersCache((prev) => ({ ...prev, [selectedBank]: offersList }));

          // Also fetch bank website link from banks collection
          const bankInfoDoc = await getDoc(doc(db, "banks", selectedBank));
          if (bankInfoDoc.exists()) {
            setBankLink(bankInfoDoc.data().address || "");
          } else {
            setBankLink("");
          }
        }
      } catch (err) {
        console.error("Error fetching offers or bank link:", err);
      }
    };

    fetchOffersAndBankLink();
  }, [selectedBank, offersCache]);

  const filteredOffers = useMemo(() => {
    if (!selectedBank || !offersCache[selectedBank]) return [];
    if (!searchTerm.trim()) return offersCache[selectedBank];

    const lower = searchTerm.toLowerCase();
    return offersCache[selectedBank].filter(
      (o) =>
        (o.vendor?.toLowerCase().includes(lower) || false) ||
        (o.offer?.toLowerCase().includes(lower) || false)
    );
  }, [searchTerm, offersCache, selectedBank]);

  const totalPages = Math.ceil(filteredOffers.length / PAGE_SIZE);
  const paginatedOffers = filteredOffers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleBankSelect = (bankId) => {
    setSelectedBank(bankId);
    setPage(1);
    setSearchTerm("");
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">Bank Offers</h1>

      {/* Bank Tiles */}
      <div className="flex flex-wrap justify-center gap-6 mb-8">
        {banksLoading ? (
          <p>Loading banks...</p>
        ) : (
          banks.map((bank) => (
            <button
              key={bank.id}
              onClick={() => handleBankSelect(bank.id)}
              className={`border rounded p-2 cursor-pointer transition-shadow ${selectedBank === bank.id
                  ? "shadow-lg border-indigo-600"
                  : "hover:shadow-md"
                }`}
              title={bank.id}
            >
              <img
                src={bank.logo || "/default-bank-logo.png"}
                alt={bank.id}
                className="w-24 h-24 object-contain"
              />
            </button>
          ))
        )}
      </div>

      {/* Search */}
      <div className="mb-2 flex justify-center">
        <input
          type="text"
          placeholder="Search offers or vendors..."
          className="border rounded px-4 py-2 w-full max-w-md"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* 🔗 Bank Website Link */}
      {bankLink && (
        <div className="text-center mb-6">
          <a
            href={bankLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Visit {selectedBank} Website
          </a>
        </div>
      )}

      {/* Offers List */}
      {paginatedOffers.length === 0 ? (
        <p className="text-center text-gray-600">No valid offers found.</p>
      ) : (
        <ul className="space-y-4">
          {paginatedOffers.map(
            ({
              id,
              vendor,
              offer,
              category,
              icon,
              expiryDttm,
              minimumSpend,
              maximumOffer,
            }) => (
              <li
                key={id || `${vendor}-${offer}`}
                className="flex items-center space-x-4 border p-4 rounded shadow bg-white"
              >
                <img
                  src={icon || "/default-icon.png"}
                  alt={`${vendor} logo`}
                  className="w-16 h-16 object-contain rounded"
                />
                <div>
                  <div className="font-semibold text-lg">
                    {vendor && vendor.includes("##") ? (category || vendor) : vendor}
                  </div>
                  <div>{offer}</div>
                  {minimumSpend && (
                    <div className="text-sm text-gray-700">
                      Minimum Spend: {formatAmount(minimumSpend)}
                    </div>
                  )}
                  {maximumOffer && (
                    <div className="text-sm text-gray-700">
                      Maximum Discount: {formatAmount(maximumOffer)}
                    </div>
                  )}
                  {expiryDttm && (
                    <div className="text-sm text-gray-500">
                      Expires: {new Date(expiryDttm.seconds ? expiryDttm.seconds * 1000 : expiryDttm).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </li>
            )
          )}
        </ul>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 rounded border bg-gray-100">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
